import React, { useEffect, useState } from "react";
import { db, } from "../../services/firebaseConfig";
import { collection, addDoc, doc, updateDoc, getDoc, serverTimestamp } from "firebase/firestore";
import AlertMessage from "../../components/AlertMessage";
import { generateRefNumber } from "../../utils/referenceNumber";
import { ORDER_STATUS } from "../../utils/orderStatus";

import "../../styles/Cart.css";


const Cart = ({ userId, onMenuSelect }) => {
    const [cartItems, setCartItems] = useState([]);
    const [error, setError] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [expiryTime, setExpiryTime] = useState(null);
    const [countdown, setCountdown] = useState(0);
    const [showAlert, setShowAlert] = useState(false);

    useEffect(() => {

        const storedCart = JSON.parse(localStorage.getItem("cart")) || { items: [], expiryTime: 0 };
        const currentTime = new Date().getTime();

        if (storedCart.expiryTime && currentTime > storedCart.expiryTime) {
            console.log("🛒 Troli telah tamat tempoh, mengosongkan...");
            localStorage.removeItem("cart");
            setCartItems([]);
            setExpiryTime(null);
        } else {
            setCartItems(storedCart.items || []);
            setExpiryTime(storedCart.expiryTime);
            setCountdown(Math.max(0, Math.floor((storedCart.expiryTime - currentTime) / 1000)));
        }
    }, []);

    useEffect(() => {
        if (!expiryTime) return;
        const interval = setInterval(() => {
            const currentTime = new Date().getTime();
            const remainingTime = Math.max(0, Math.floor((expiryTime - currentTime) / 1000));
            setCountdown(remainingTime);

            if (remainingTime === 0) {
                console.log("🛒 Tempahan tamat. Mengembalikan stok...");
                localStorage.removeItem("cart");
                setCartItems([]);
                setExpiryTime(null);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [expiryTime]);

    const logStockMovement = async (item, qty, status, district) => {
        await addDoc(collection(db, "inventori", "selangor", "stockLogs"), {
            itemName: item.name,
            category: item.category || "-",
            variant: item.variant || "-", // saiz/tahun kalau ada
            quantity: qty,
            action: status,
            district,
            timestamp: serverTimestamp(),
        });
    };


    const removeFromCart = async (productId, variant) => {
        const updatedCart = cartItems.map((item) => {
            if (item.id === productId) {
                if (item.sizes && item.sizes[variant] !== undefined) {
                    restoreStock(productId, variant, item.sizes[variant], "sizeQuantities");
                    delete item.sizes[variant];
                    return Object.keys(item.sizes).length > 0 ? item : null;
                }
                if (item.years && item.years[variant] !== undefined) {
                    restoreStock(productId, variant, item.years[variant], "yearQuantities");
                    delete item.years[variant];
                    return Object.keys(item.years).length > 0 ? item : null;
                }
                if (variant === "default" && item.quantity !== undefined) {
                    restoreStock(productId, null, item.quantity, "quantity");
                    return null;
                }
            }
            return item;
        }).filter(Boolean);

        setCartItems(updatedCart);
        localStorage.setItem("cart", JSON.stringify({ items: updatedCart, expiryTime }));
    };

    // Hybrid restoreStock
    const restoreStock = async (productId, variant, qty, field) => {
        const stockRef = doc(db, "inventori", "selangor", "stocks", productId);
        const stockSnap = await getDoc(stockRef);
        if (!stockSnap.exists()) return;

        const stockData = stockSnap.data();
        if (field === "sizeQuantities") {
            const updatedQty = (stockData.sizeQuantities?.[variant] || 0) + qty;
            await updateDoc(stockRef, { [`sizeQuantities.${variant}`]: updatedQty });
        } else if (field === "yearQuantities") {
            const updatedQty = (stockData.yearQuantities?.[variant] || 0) + qty;
            await updateDoc(stockRef, { [`yearQuantities.${variant}`]: updatedQty });
        } else if (field === "quantity") {
            const updatedQty = (stockData.quantity || 0) + qty;
            await updateDoc(stockRef, { quantity: updatedQty });
        }
    };


    const updateQuantity = async (productId, variant, newQty) => {
        const stockRef = doc(db, "inventori", "selangor", "stocks", productId);
        const stockSnap = await getDoc(stockRef);
        if (!stockSnap.exists()) return;

        const stockData = stockSnap.data();
        let availableQty, cartItem, currentQty;

        cartItem = cartItems.find(item => item.id === productId);
        if (!cartItem) return;

        // Baju (by size) / Buku (by year)
        if (cartItem.sizes && cartItem.sizes[variant] !== undefined) {
            availableQty = stockData.sizeQuantities?.[variant] ?? 0;
            currentQty = cartItem.sizes[variant];
        } else if (cartItem.years && cartItem.years[variant] !== undefined) {
            availableQty = stockData.yearQuantities?.[variant] ?? 0;
            currentQty = cartItem.years[variant];
        } else if (variant === "default" && cartItem.quantity !== undefined) {
            availableQty = stockData.quantity ?? 0;
            currentQty = cartItem.quantity;
        } else {
            return;
        }

        if (newQty > currentQty + availableQty) {
            alert("Stok tidak mencukupi!");
            return;
        }

        const difference = newQty - currentQty;
        const updatedStockQty = availableQty - difference;

        // Update Firestore ikut kategori
        if (cartItem.sizes && cartItem.sizes[variant] !== undefined) {
            await updateDoc(stockRef, { [`sizeQuantities.${variant}`]: updatedStockQty });
        } else if (cartItem.years && cartItem.years[variant] !== undefined) {
            await updateDoc(stockRef, { [`yearQuantities.${variant}`]: updatedStockQty });
        } else if (variant === "default" && cartItem.quantity !== undefined) {
            await updateDoc(stockRef, { quantity: updatedStockQty });
        }

        // Update cartItems local
        const updatedCart = cartItems.map(item => {
            if (item.id === productId) {
                if (item.sizes && item.sizes[variant] !== undefined) {
                    return { ...item, sizes: { ...item.sizes, [variant]: newQty } };
                } else if (item.years && item.years[variant] !== undefined) {
                    return { ...item, years: { ...item.years, [variant]: newQty } };
                } else if (variant === "default" && item.quantity !== undefined) {
                    return { ...item, quantity: newQty };
                }
            }
            return item;
        });

        setCartItems(updatedCart);
        localStorage.setItem("cart", JSON.stringify({ items: updatedCart, expiryTime }));
    };



    const confirmOrder = async () => {
        if (!userId) {
            setError("Pengguna tidak sah. Sila log masuk semula.");
            return;
        }

        if (cartItems.length === 0) {
            setError("Troli kosong! Sila tambah item sebelum meneruskan.");
            return;
        }

        setIsProcessing(true);
        try {
            const userRef = doc(db, "inventori", "selangor", "users", userId);
            const userSnap = await getDoc(userRef);

            let branchName = "Tidak Diketahui";
            let district = "Tidak Diketahui";

            if (userSnap.exists()) {
                const userData = userSnap.data();
                branchName = userData.branchName || "Tidak Diketahui";
                district = userData.district || "Tidak Diketahui";
            }

            const orderNumber = await generateRefNumber("OR", district);

            // =========================
            // PATCH: Ambil snapshot harga untuk setiap item dalam cart
            // =========================
            const itemsWithHarga = await Promise.all(cartItems.map(async (item) => {
                const stockRef = doc(db, "inventori", "selangor", "stocks", item.id);
                const stockSnap = await getDoc(stockRef);

                let hargaSnapshot = {};

                if (stockSnap.exists()) {
                    const stockData = stockSnap.data();
                    if (item.sizes) {
                        // Untuk kategori baju (by size)
                        hargaSnapshot = {};
                        for (const [size, qty] of Object.entries(item.sizes)) {
                            hargaSnapshot[size] = {
                                hargaKos: stockData.harga?.[size]?.kos ?? null,
                                hargaPengerusi: stockData.harga?.[size]?.pengerusi ?? null,
                                hargaPengurus: stockData.harga?.[size]?.pengurus ?? null,
                                hargaJual: item.hargaPerSize?.[size] ?? item.price ?? null, // <-- KUNCI UTAMA
                                qty
                            };
                        }
                    }
                    else if (item.years) {
                        // Untuk buku (by year)
                        hargaSnapshot = {};
                        for (const [year, qty] of Object.entries(item.years)) {
                            hargaSnapshot[year] = {
                                hargaKos: stockData.harga?.[year]?.kos ?? null,
                                hargaPengerusi: stockData.harga?.[year]?.pengerusi ?? null,
                                hargaPengurus: stockData.harga?.[year]?.pengurus ?? null,
                                hargaJual: item.hargaPerYear?.[year] ?? item.price ?? null,
                                qty
                            };
                        }
                    } else if (item.quantity) {
                        // Untuk runcit/umum
                        hargaSnapshot = {
                            hargaKos: stockData.harga?.kos ?? null,
                            hargaPengerusi: stockData.harga?.pengerusi ?? null,
                            hargaPengurus: stockData.harga?.pengurus ?? null,
                            hargaJual: item.price,
                            qty: item.quantity
                        };
                    }
                }
                // Simpan snapshot harga sebagai field "hargaSnapshot"
                return { ...item, hargaSnapshot };
            }));

            // =========================
            // Simpan order beserta harga snapshot
            // =========================
            await addDoc(collection(db, "inventori", "selangor", "orders"), {
                orderNumber,
                branchName,
                district,
                userId,
                items: itemsWithHarga, // <-- Simpan dengan harga snapshot
                orderDate: new Date(),
                status: ORDER_STATUS.ORDER_CONFIRMED,
            });

            for (const item of cartItems) {
                if (item.quantity) {
                    await logStockMovement(item, item.quantity, "keluar", district);
                } else if (item.sizes) {
                    for (const [size, qty] of Object.entries(item.sizes)) {
                        await logStockMovement(item, qty, `keluar (${size})`, district);
                    }
                } else if (item.years) {
                    for (const [year, qty] of Object.entries(item.years)) {
                        await logStockMovement(item, qty, `keluar (${year})`, district);
                    }
                }
            }

            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 2000);
            setCartItems([]);
            localStorage.removeItem("cart");
            setExpiryTime(null);
            setError("");
        } catch (error) {
            console.error("❌ Ralat semasa mengesahkan tempahan:", error);
            setError("Gagal menghantar tempahan. Sila cuba lagi.");
        }
        setIsProcessing(false);
    };


    const calculateTotal = () => {
        return cartItems.reduce((total, item) => {
            let itemTotal = 0;

            if (item.sizes) {
                itemTotal = Object.entries(item.sizes).reduce((sum, [size, qty]) => {
                    const harga = item.hargaPerSize?.[size] ?? item.price;
                    return sum + (harga * qty);
                }, 0);
            }
            else if (item.years) {
                itemTotal = Object.entries(item.years).reduce((sum, [year, qty]) => {
                    const harga = item.hargaPerYear?.[year] ?? item.price;
                    return sum + (harga * qty);
                }, 0);
            }
            else if (item.quantity) {
                itemTotal = item.price * item.quantity;
            }

            return total + itemTotal;
        }, 0).toFixed(2);
    };

    return (
        <div className="cart-container">
            <AlertMessage message="Tempahan Berjaya!" isVisible={showAlert} />

            <h2>Troli</h2>

            {error && <p className="error-message">{error}</p>}
            {cartItems.length === 0 ? (
                <p>Tiada item dalam troli.</p>
            ) : (
                <>
                    <p>Sila sahkan tempahan dalam : {Math.floor(countdown / 60)} minit {countdown % 60} saat</p>

                    <ul className="cart-list">
                        {cartItems.map((item) => (
                            <li key={item.id} className="cart-item">
                                <div className="cart-item-info">
                                    <img src={item.imageUrl} alt={item.name} className="cart-item-image" />
                                    <div className="cart-item-details">
                                        <span className="cart-item-name">{item.name}</span>
                                        {item.sizes && Object.entries(item.sizes).map(([size, qty]) => (
                                            <div key={size} className="cart-size">
                                                <span style={{ width: "50px" }}>{size}: </span>
                                                <button onClick={() => updateQuantity(item.id, size, qty - 1)} disabled={qty <= 1}>➖</button>
                                                <span> {qty} pcs </span>
                                                <span className="cart-item-total">
                                                    (RM{((item.hargaPerSize?.[size] ?? item.price) * qty).toFixed(2)})
                                                </span>
                                                <button onClick={() => updateQuantity(item.id, size, qty + 1)}>➕</button>
                                                <button className="remove-btn" onClick={() => removeFromCart(item.id, size)}>❌</button>
                                            </div>
                                        ))}

                                        {item.years && Object.entries(item.years).map(([year, qty]) => (
                                            <div key={year} className="cart-size">
                                                <span>{year}: </span>
                                                <button onClick={() => updateQuantity(item.id, year, qty - 1)} disabled={qty <= 1}>➖</button>
                                                <span> {qty} pcs </span>
                                                <span className="cart-item-total">
                                                    (RM{((item.hargaPerYear?.[year] ?? item.price) * qty).toFixed(2)})
                                                </span>
                                                <button onClick={() => updateQuantity(item.id, year, qty + 1)}>➕</button>
                                                <button className="remove-btn" onClick={() => removeFromCart(item.id, year)}>❌</button>
                                            </div>
                                        ))}

                                        {item.quantity && (
                                            <div className="cart-size">
                                                <span>Kuantiti: </span>
                                                <button onClick={() => updateQuantity(item.id, "default", item.quantity - 1)} disabled={item.quantity <= 1}>➖</button>
                                                <span> {item.quantity} pcs </span>
                                                <span className="cart-item-total"> (RM{(item.price * item.quantity).toFixed(2)}) </span>
                                                <button onClick={() => updateQuantity(item.id, "default", item.quantity + 1)}>➕</button>
                                                <button className="remove-btn" onClick={() => removeFromCart(item.id, "default")}>❌</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="cart-total">
                        <h3>Jumlah Keseluruhan: RM{calculateTotal()}</h3>
                    </div>

                    <button className="confirm-btn" onClick={confirmOrder} disabled={isProcessing}>
                        {isProcessing ? "Memproses..." : "Sahkan"}
                    </button>
                    <button className="back-btn" onClick={() => onMenuSelect("Kedai")}>
                        Tambah Lagi
                    </button>
                </>
            )}
        </div>
    );
};

export default Cart;
